// src/utils/categoryAdapter.js

export function slugifyCategory(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

export function getCategoryValue(value = "") {
  return String(value || "").trim();
}

export function getCategoryLabel(value = "") {
  const raw = getCategoryValue(value);
  if (!raw) return "";

  // Si ya viene como texto humano, lo respetamos bastante.
  // Si viene como key tipo "trail-running", lo mostramos como "Trail Running".
  const text = raw.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();

  return text
    .split(" ")
    .map((word) => {
      if (!word) return word;
      if (word === "&" || word === "/") return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export function sameCategory(a = "", b = "") {
  const av = getCategoryValue(a);
  const bv = getCategoryValue(b);

  if (!av || !bv) return false;
  if (av === bv) return true;
  if (av.toLowerCase() === bv.toLowerCase()) return true;

  return slugifyCategory(av) === slugifyCategory(bv);
}

export function uniqueCategoryItems(values = []) {
  const map = new Map();

  (Array.isArray(values) ? values : []).forEach((value) => {
    const raw = getCategoryValue(value);
    if (!raw) return;

    const key = slugifyCategory(raw) || raw.toLowerCase();
    if (!map.has(key)) {
      map.set(key, {
        value: raw,
        slug: key,
        label: getCategoryLabel(raw),
      });
    }
  });

  return Array.from(map.values()).sort((a, b) =>
    a.label.localeCompare(b.label, "es")
  );
}

export function buildCategoryHierarchy(
  items = [],
  { getCategory = () => "", getSubcategory = () => "" } = {},
) {
  const categoryMap = new Map();

  (Array.isArray(items) ? items : []).forEach((item) => {
    const categoryValue = getCategoryValue(getCategory(item));
    if (!categoryValue) return;

    const categoryKey = slugifyCategory(categoryValue) || categoryValue.toLowerCase();
    if (!categoryMap.has(categoryKey)) {
      categoryMap.set(categoryKey, {
        value: categoryValue,
        slug: categoryKey,
        label: getCategoryLabel(categoryValue),
        count: 0,
        subcategoryMap: new Map(),
      });
    }

    const category = categoryMap.get(categoryKey);
    category.count += 1;

    const subcategoryValue = getCategoryValue(getSubcategory(item));
    if (!subcategoryValue) return;

    const subcategoryKey =
      slugifyCategory(subcategoryValue) || subcategoryValue.toLowerCase();
    if (!category.subcategoryMap.has(subcategoryKey)) {
      category.subcategoryMap.set(subcategoryKey, {
        value: subcategoryValue,
        slug: subcategoryKey,
        label: getCategoryLabel(subcategoryValue),
        count: 0,
      });
    }

    category.subcategoryMap.get(subcategoryKey).count += 1;
  });

  return Array.from(categoryMap.values())
    .map(({ subcategoryMap, ...category }) => ({
      ...category,
      subcategories: Array.from(subcategoryMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label, "es"),
      ),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "es"));
}

export function normalizeCategoryMetadata(payload = {}) {
  const source = Array.isArray(payload?.categories)
    ? payload.categories
    : Array.isArray(payload?.items)
      ? payload.items
      : [];

  return source
    .map((category) => {
      const value = getCategoryValue(
        typeof category === "string"
          ? category
          : category?.key || category?.value || category?.name || category?.label,
      );
      if (!value) return null;

      const rawSubcategories = Array.isArray(category?.subcategories)
        ? category.subcategories
        : Array.isArray(category?.subcategorias)
          ? category.subcategorias
          : [];
      const subcategories = rawSubcategories
        .map((subcategory) => {
          const subValue = getCategoryValue(
            typeof subcategory === "string"
              ? subcategory
              : subcategory?.key || subcategory?.value || subcategory?.name || subcategory?.label,
          );
          if (!subValue) return null;
          return {
            value: subValue,
            slug: slugifyCategory(subValue) || subValue.toLowerCase(),
            label:
              (typeof subcategory === "object" && (subcategory?.name || subcategory?.label)) ||
              getCategoryLabel(subValue),
            count: Number(subcategory?.count || 0),
          };
        })
        .filter(Boolean)
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "es"));

      return {
        value,
        slug: slugifyCategory(value) || value.toLowerCase(),
        label:
          (typeof category === "object" && (category?.name || category?.label)) ||
          getCategoryLabel(value),
        count: Number(category?.count || 0),
        image: String(category?.sampleImage || category?.image || ""),
        subcategories,
      };
    })
    .filter(Boolean);
}
